import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { allergensList } from "../../../utilities/allergensList";

const initialValues = {
  itemName: "",
  itemPrice: "",
  itemDescription: "",
  itemIngredients: "",
  itemImage: null,
  itemAllergens: allergensList.map(({ code }) => ({
    allergenName: code,
    mayContain: false,
    included: false,
    alternativeAvailable: false,
    alternativeDetails: "",
  })),
};

const validationSchema = Yup.object({
  itemName: Yup.string().required("Please include the menu item name"),
  itemPrice: Yup.number()
    .required("Please include a price")
    .positive("Please include a price of at least 0.01"),
  itemDescription: Yup.string().required("Please add a description to your menu item"),
  itemIngredients: Yup.string().required("Please include the ingredients for your menu item"),
  itemImage: Yup.mixed()
    .required("Item image is required")
    .test("fileSize", "Image too large. Maximum size is 10MB", (v) => !v || v.size <= 10 * 1024 * 1024),
});

// ── Shared field styles ────────────────────────────────────────────────────

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  color: "var(--ink-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginBottom: "6px",
};

const inputStyle = {
  display: "block",
  width: "100%",
  maxWidth: "480px",
  padding: "8px 10px",
  fontSize: "13px",
  color: "var(--ink)",
  background: "var(--white)",
  border: "1px solid var(--border)",
  borderRadius: "7px",
  fontFamily: "inherit",
  outline: "none",
};

const errorStyle = {
  fontSize: "12px",
  color: "var(--accent)",
  marginTop: "4px",
};

// ── Component ──────────────────────────────────────────────────────────────

const MenuItemForm = ({ businessId, menuId, fetchMenuData, setShowCreateForm }) => {
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (values) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("itemImage", values.itemImage);
    formData.append("itemName", values.itemName);
    formData.append("itemPrice", values.itemPrice);
    formData.append("itemDescription", values.itemDescription);
    formData.append("itemIngredients", values.itemIngredients);

    const filteredAllergens = values.itemAllergens.filter((a) => a.mayContain || a.included);
    formData.append("itemAllergens", JSON.stringify(filteredAllergens));

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/menuitem/${businessId}/${menuId}`,
      { method: "POST", credentials: "include", body: formData }
    );

    setIsLoading(false);
    if (response.ok) {
      fetchMenuData();
      setShowCreateForm(false);
    }
  };

  return (
    <div
      style={{
        background: "var(--white)",
        border: "1px solid var(--border)",
        borderRadius: "12px",
        padding: "24px",
        marginBottom: "16px",
        boxShadow: "var(--shadow)",
      }}
    >
      <h2
        style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: "20px",
          color: "var(--ink)",
          marginBottom: "20px",
        }}
      >
        New Menu Item
      </h2>

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {({ values, setFieldValue }) => (
          <Form style={{ display: "flex", flexDirection: "column", gap: "18px" }}>

            {/* Item Name */}
            <div>
              <label htmlFor="itemName" style={labelStyle}>Item Name</label>
              <Field type="text" name="itemName" id="itemName" style={inputStyle} />
              <ErrorMessage name="itemName" component="div" style={errorStyle} />
            </div>

            {/* Item Image */}
            <div>
              <label htmlFor="itemImage" style={labelStyle}>Item Image</label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                id="itemImage"
                style={inputStyle}
                onChange={(e) => setFieldValue("itemImage", e.currentTarget.files[0])}
              />
              <ErrorMessage name="itemImage" component="div" style={errorStyle} />
            </div>

            {/* Item Price */}
            <div>
              <label htmlFor="itemPrice" style={labelStyle}>Price (£)</label>
              <Field type="number" name="itemPrice" id="itemPrice" step="0.01" style={{ ...inputStyle, maxWidth: "160px" }} />
              <ErrorMessage name="itemPrice" component="div" style={errorStyle} />
            </div>

            {/* Item Description */}
            <div>
              <label htmlFor="itemDescription" style={labelStyle}>Description</label>
              <Field as="textarea" name="itemDescription" id="itemDescription" rows={3} style={{ ...inputStyle, resize: "vertical" }} />
              <ErrorMessage name="itemDescription" component="div" style={errorStyle} />
            </div>

            {/* Item Ingredients */}
            <div>
              <label htmlFor="itemIngredients" style={labelStyle}>Ingredients</label>
              <Field as="textarea" name="itemIngredients" id="itemIngredients" rows={3} style={{ ...inputStyle, resize: "vertical" }} />
              <ErrorMessage name="itemIngredients" component="div" style={errorStyle} />
            </div>

            {/* ── Allergens table ── */}
            <div>
              <p style={{ ...labelStyle, marginBottom: "10px" }}>Allergens</p>
              <p style={{ fontSize: "12px", color: "var(--ink-muted)", marginBottom: "12px" }}>
                Only allergens marked "May Contain" or "Included" will be saved.
              </p>

              <div style={{ overflowX: "auto", borderRadius: "8px", border: "1px solid var(--border)" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ background: "var(--cream)", borderBottom: "2px solid var(--border)" }}>
                      <th style={{ padding: "10px 14px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
                        Allergen
                      </th>
                      {["May Contain?", "Included?", "Alternative Available?", "Alternative Details"].map((h) => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: "center", fontSize: "11px", fontWeight: 700, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {values.itemAllergens.map((allergen, index) => {
                      const displayName = allergensList[index]?.name || allergen.allergenName;
                      const rowBg = index % 2 === 0 ? "var(--white)" : "#F5F7F6";

                      return (
                        <tr key={allergen.allergenName} style={{ background: rowBg, borderBottom: "1px solid var(--border)" }}>
                          {/* Allergen name */}
                          <td style={{ padding: "10px 14px", fontWeight: 500, color: "var(--ink)", whiteSpace: "nowrap" }}>
                            {displayName}
                          </td>

                          {/* May Contain */}
                          <td style={{ padding: "10px 14px", textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={allergen.mayContain}
                              onChange={(e) => setFieldValue(`itemAllergens.${index}.mayContain`, e.target.checked)}
                              style={{ width: "16px", height: "16px", accentColor: "var(--brand)", cursor: "pointer" }}
                            />
                          </td>

                          {/* Included */}
                          <td style={{ padding: "10px 14px", textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={allergen.included}
                              onChange={(e) => setFieldValue(`itemAllergens.${index}.included`, e.target.checked)}
                              style={{ width: "16px", height: "16px", accentColor: "var(--brand)", cursor: "pointer" }}
                            />
                          </td>

                          {/* Alternative Available */}
                          <td style={{ padding: "10px 14px", textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={allergen.alternativeAvailable}
                              onChange={(e) => setFieldValue(`itemAllergens.${index}.alternativeAvailable`, e.target.checked)}
                              style={{ width: "16px", height: "16px", accentColor: "var(--brand)", cursor: "pointer" }}
                            />
                          </td>

                          {/* Alternative Details */}
                          <td style={{ padding: "8px 14px" }}>
                            <input
                              type="text"
                              value={allergen.alternativeDetails}
                              disabled={!allergen.alternativeAvailable}
                              onChange={(e) => setFieldValue(`itemAllergens.${index}.alternativeDetails`, e.target.value)}
                              placeholder={allergen.alternativeAvailable ? "Describe the alternative…" : ""}
                              style={{
                                width: "100%",
                                minWidth: "160px",
                                padding: "5px 8px",
                                fontSize: "12px",
                                border: "1px solid var(--border)",
                                borderRadius: "5px",
                                background: allergen.alternativeAvailable ? "var(--white)" : "var(--cream)",
                                color: allergen.alternativeAvailable ? "var(--ink)" : "var(--ink-muted)",
                                fontFamily: "inherit",
                                outline: "none",
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: "8px", paddingTop: "4px" }}>
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  padding: "9px 20px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 600,
                  background: "var(--brand)",
                  color: "white",
                  border: "none",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  opacity: isLoading ? 0.7 : 1,
                  fontFamily: "inherit",
                }}
              >
                {isLoading ? "Saving…" : "Save item"}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                style={{
                  padding: "9px 20px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 600,
                  background: "none",
                  border: "1px solid var(--border)",
                  color: "var(--ink-soft)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
            </div>

          </Form>
        )}
      </Formik>
    </div>
  );
};

export default MenuItemForm;
